// Cloud Functions configuration and URL mapping

export interface CloudFunctionConfig {
  baseUrl: string;
  functions: {
    // Problem functions
    getProblems: string;
    generateProblem: string;
    searchOrGenerate: string;
    getProblemById: string;
    getProblemsByIds: string; // Batch fetch
    updateProblem: string;
    deleteProblem: string;
    getStats: string;
    verifySolution: string;
    verifyReview: string;
    
    // Collection functions
    getCollections: string;
    getSystemCollections: string;
    getCollection: string;
    createCollection: string;
    createSystemCollection: string;
    updateCollection: string;
    deleteCollection: string;
    updateCollectionProblems: string;
    generateCollectionByAI: string;
    updateCollectionOrder: string;
    
    // Auth functions
    getUserProfile: string;
    updateUserPreferences: string;
    saveUserSolution: string;
    getUserSolutions: string;
    getUserSolutionByProblem: string;
    toggleCompletionStatus: string;
    getCompletionStatus: string;
    getAllCompletionStatus: string;
    getChatHistory: string;
    sendChatMessage: string;
    saveChatHistory: string;
    
    // Upload functions
    uploadCsv: string;
    processCsvBatch: string;
    getUploadSession: string;
    transcribeAudio: string;
    
    // Refresh functions
    refreshSection: string;
    getRefreshHistory: string;
    getRefreshStats: string;
    
    // Diagnostic functions
    testEnvironment: string;
    testClaudeApi: string;
    diagnosticHealth: string;
    
    // Health checks
    authHealth: string;
    uploadHealth: string;
    refreshHealth: string;
  };
}

// Environment detection
const isDevelopment = window.location.hostname === "localhost" ||
                     window.location.hostname === "127.0.0.1";

const isProductionEnvironment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'aiprep.io' ||
         hostname === 'www.aiprep.io' ||
         hostname === 'app.aiprep.io';
};

// Project IDs for different environments
const PROJECT_IDS = {
  development: 'aiprep-465906',
  production: 'aiprep-prod',
} as const;

// Get the current project ID based on environment
const getProjectId = (): string => {
  if (isProductionEnvironment()) {
    return PROJECT_IDS.production;
  }
  return PROJECT_IDS.development;
};

// Cloud Functions base URLs - environment-aware
const getCloudFunctionsUrl = (): string => {
  if (isDevelopment) {
    // Local development - use emulator with dev project
    return `http://127.0.0.1:5001/${PROJECT_IDS.development}/us-central1`;
  }
  // Deployed environment - use appropriate project
  const projectId = getProjectId();
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

export const cloudFunctionsConfig: CloudFunctionConfig = {
  baseUrl: getCloudFunctionsUrl(),
  functions: {
    // Problem functions (v2)
    getProblems: "getProblems",
    generateProblem: "generateProblem", 
    searchOrGenerate: "searchOrGenerate",
    getProblemById: "getProblemById",
    getProblemsByIds: "getProblemsByIds", // TODO: Needs v2 migration
    updateProblem: "updateProblem",
    deleteProblem: "deleteProblem",
    getStats: "getStats",
    verifySolution: "verifySolution",
    verifyReview: "verifyReview",
    
    // Collection functions
    getCollections: "getCollections",
    getSystemCollections: "getSystemCollections",
    getCollection: "getCollection",
    createCollection: "createCollection",
    createSystemCollection: "createSystemCollection",
    updateCollection: "updateCollection",
    deleteCollection: "deleteCollection",
    updateCollectionProblems: "updateCollectionProblems",
    generateCollectionByAI: "generateCollectionByAI",
    updateCollectionOrder: "updateCollectionOrder",
    
    // Auth functions
    getUserProfile: "getUserProfile",
    updateUserPreferences: "updateUserPreferences",  // Updated to match backend export
    saveUserSolution: "saveUserSolution",
    getUserSolutions: "getUserSolutions",
    getUserSolutionByProblem: "getUserSolutionByProblem",
    toggleCompletionStatus: "toggleCompletionStatus",
    getCompletionStatus: "getCompletionStatus",
    getAllCompletionStatus: "getAllCompletionStatus",
    getChatHistory: "getChatHistory",
    sendChatMessage: "sendChatMessage",
    saveChatHistory: "saveChatHistory",
    
    // Upload functions  
    uploadCsv: "uploadCsv",
    processCsvBatch: "processCsvBatch",
    getUploadSession: "getUploadSession",
    transcribeAudio: "transcribeAudio",
    
    // Refresh functions
    refreshSection: "refreshSection",
    getRefreshHistory: "getRefreshHistory", 
    getRefreshStats: "getRefreshStats",
    
    // Diagnostic functions
    testEnvironment: "testEnvironment",
    testClaudeApi: "testClaudeApi",
    diagnosticHealth: "diagnosticHealth",
    
    // Health checks
    authHealth: "authHealth",
    uploadHealth: "uploadHealth",
    refreshHealth: "refreshHealth",
  }
};

// Helper function to build Cloud Function URL
export const buildCloudFunctionUrl = (functionName: string, path?: string): string => {
  const baseUrl = cloudFunctionsConfig.baseUrl;
  const url = `${baseUrl}/${functionName}`;
  return path ? `${url}${path}` : url;
};

// URL mapping helpers for backward compatibility
export const getEndpointUrl = (endpoint: keyof CloudFunctionConfig['functions'], path?: string): string => {
  const functionName = cloudFunctionsConfig.functions[endpoint];
  return buildCloudFunctionUrl(functionName, path);
};